import { Link } from "react-router-dom";
import useNoIndex from "@/hooks/useNoIndex";

export default function TermsOfServicePage() {
  useNoIndex();

  const lastUpdated = "March 17, 2026";

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <Link to="/" className="text-sm text-teal-600 hover:text-teal-700">
          ← Back to home
        </Link>

        <h1 className="text-4xl md:text-5xl font-bold mt-4 mb-3">Terms of Service</h1>
        <p className="text-slate-600 mb-10">Last updated: {lastUpdated}</p>

        <div className="space-y-8 text-slate-700 leading-7">
          <section>
            <p>
              Welcome to CreaVideos.online (the “Website”), owned and operated by CreaVideos – Renaud
              Jean Noel Pomies (“CreaVideos”, “we”, “our”, or “us”).
            </p>
            <p className="mt-3">
              By accessing, browsing, creating an account, subscribing to, or using the Website and
              its services, you agree to be bound by these Terms of Service (“Terms”), as well as our
              <Link to="/license-agreement" className="text-teal-600 hover:text-teal-700">License Agreement</Link> and <Link to="/privacy-policy" className="text-teal-600 hover:text-teal-700">Privacy Policy</Link>, which are incorporated by reference. If you do not
              agree, you must not use the Website.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">1. Eligibility and Acceptance</h2>
            <p>By using the Website, you represent and warrant that:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>you are at least 18 years old or have legal authorization,</li>
              <li>you have the legal capacity to enter into a binding agreement,</li>
              <li>if acting on behalf of a company, you have authority to bind that entity.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">2. Our Service</h2>
            <p>CreaVideos provides a subscription-based platform that allows users to:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>access video templates,</li>
              <li>customize templates (text, images, videos),</li>
              <li>render and download finished videos,</li>
              <li>use related tools and features.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">3. Account Registration and Security</h2>
            <p>You must create an account to access certain features.</p>
            <p className="mt-2">You agree to:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>provide accurate and complete information,</li>
              <li>keep your login credentials secure,</li>
              <li>be responsible for all activity under your account.</li>
            </ul>
            <p className="mt-2">Account sharing is prohibited.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">4. Subscription Plans, Billing, and Renewal</h2>

            <h3 className="text-xl font-semibold text-slate-900 mt-4 mb-2">4.1 Subscription Plans</h3>
            <p>Access is provided through subscription plans described on the Website.</p>

            <h3 className="text-xl font-semibold text-slate-900 mt-4 mb-2">4.2 Billing</h3>
            <p>You agree to pay all fees shown at purchase, including applicable taxes.</p>

            <h3 className="text-xl font-semibold text-slate-900 mt-4 mb-2">4.3 Automatic Renewal</h3>
            <p>Subscriptions renew automatically unless cancelled before the renewal date.</p>

            <h3 className="text-xl font-semibold text-slate-900 mt-4 mb-2">4.4 Pricing Changes</h3>
            <p>We may change pricing. Changes apply at your next renewal.</p>

            <h3 className="text-xl font-semibold text-slate-900 mt-4 mb-2">4.5 Failed Payments</h3>
            <p>Failed payments may result in suspension or termination of access.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">5. Cancellation and Refunds</h2>

            <h3 className="text-xl font-semibold text-slate-900 mt-4 mb-2">5.1 Cancellation</h3>
            <p>You may cancel anytime via your account or by contacting us.</p>

            <h3 className="text-xl font-semibold text-slate-900 mt-4 mb-2">5.2 Refund Policy</h3>
            <p>All purchases are non-refundable, except where required by law.</p>

            <h3 className="text-xl font-semibold text-slate-900 mt-4 mb-2">5.3 Effect of Cancellation</h3>
            <p>Access continues until the end of your billing period.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">6. License Types and Usage Rights</h2>
            <p>You are granted a non-exclusive, non-transferable, non-sublicensable license.</p>

            <h3 className="text-xl font-semibold text-slate-900 mt-4 mb-2">A. Standard License</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Commercial use allowed</li>
              <li>Up to 500,000 total views</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-900 mt-4 mb-2">B. Enhanced License</h3>
            <p>Required if:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>views exceed 500,000</li>
              <li>used for TV, broadcast, or large-scale distribution</li>
            </ul>
            <p className="mt-2">
              Contact: <a className="text-teal-600 hover:text-teal-700" href="mailto:reno@creavideos.online">reno@creavideos.online</a>
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">7. Template Access and Modification</h2>
            <p>You may:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>customize templates,</li>
              <li>use rendered videos for your business or clients.</li>
            </ul>
            <p className="mt-3">You may NOT:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>resell or distribute templates,</li>
              <li>claim ownership of templates,</li>
              <li>build competing template platforms.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">8. User Content and Responsibilities</h2>
            <p>You are responsible for all content you upload.</p>
            <p className="mt-2">You confirm you have all rights to:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>images,</li>
              <li>videos,</li>
              <li>text,</li>
              <li>logos.</li>
            </ul>
            <p className="mt-2">You are responsible for compliance with laws and platform policies.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">9. Prohibited Uses</h2>
            <p>You may not:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>violate laws,</li>
              <li>infringe rights,</li>
              <li>upload harmful content,</li>
              <li>reverse engineer or scrape the service,</li>
              <li>bypass protections,</li>
              <li>share accounts,</li>
              <li>misuse templates.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">10. Intellectual Property</h2>
            <p>
              All templates, software, and content belong to CreaVideos. You receive a license, not
              ownership.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">11. Feedback</h2>
            <p>Any feedback you provide may be used freely without compensation.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">12. Service Availability and Changes</h2>
            <p>We may modify or discontinue services at any time.</p>
            <p className="mt-2">No guarantee of uptime or error-free operation.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">13. Third-Party Services</h2>
            <p>The Website may rely on third-party providers to deliver certain features.</p>
            <p className="mt-2">These include:</p>

            <h3 className="text-xl font-semibold text-slate-900 mt-4 mb-2">13.1 Payment Processing (PayPal)</h3>
            <p>Payments are processed through PayPal.</p>
            <p className="mt-2">We do not store full payment details.</p>
            <p className="mt-2">By making a purchase, you agree to PayPal’s terms and privacy policy.</p>

            <h3 className="text-xl font-semibold text-slate-900 mt-4 mb-2">13.2 AI Video Generation (PixVerse)</h3>
            <p>
              CreaVideos uses third-party AI services, including PixVerse, to generate videos from
              images.
            </p>
            <p className="mt-2">By using these features, you acknowledge that:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>your uploaded images, prompts, and content may be processed by PixVerse,</li>
              <li>this processing is required to deliver the service,</li>
              <li>CreaVideos is not responsible for the output generated by AI systems.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">14. Privacy</h2>
            <p>Use of the Website is subject to our Privacy Policy.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">15. Termination and Suspension</h2>
            <p>We may suspend or terminate your account if:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>you violate these Terms,</li>
              <li>payment fails,</li>
              <li>misuse occurs.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">16. Disclaimers</h2>
            <p>The service is provided “as is” and “as available”.</p>
            <p className="mt-2">We do not guarantee:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>uninterrupted service,</li>
              <li>error-free operation,</li>
              <li>platform acceptance of your videos.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">17. Limitation of Liability</h2>
            <p>We are not liable for indirect or consequential damages.</p>
            <p className="mt-2">Our total liability is limited to the amount you paid in the last 6 months.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">18. Indemnification</h2>
            <p>You agree to indemnify CreaVideos for any claims resulting from:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>your use of the service,</li>
              <li>your content,</li>
              <li>your violations of these Terms.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">19. Governing Law</h2>
            <p>These Terms are governed by the laws of Spain.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">20. Consumer Rights</h2>
            <p>Nothing limits your rights under applicable consumer laws.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">21. Changes to Terms</h2>
            <p>We may update these Terms at any time.</p>
            <p className="mt-2">Continued use = acceptance.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">22. Entire Agreement</h2>
            <p>These Terms + License + Privacy Policy = full agreement.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">23. Severability and Waiver</h2>
            <p>If a part is invalid, the rest remains enforceable.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">24. Facebook / Instagram Login and Integration</h2>
            <p>
              CreaVideos may allow users to connect accounts via Facebook or Instagram (“Meta
              Platforms”) for login, account linking, or sharing content.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mt-4 mb-2">24.1 Data Access</h3>
            <p>We may access:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>name,</li>
              <li>email,</li>
              <li>profile picture,</li>
              <li>account ID,</li>
              <li>other authorized data.</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-900 mt-4 mb-2">24.2 Use of Data</h3>
            <p>Used only to:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>authenticate users,</li>
              <li>enable login,</li>
              <li>allow sharing,</li>
              <li>operate and improve the service.</li>
            </ul>
            <p className="mt-2">We do not sell this data.</p>

            <h3 className="text-xl font-semibold text-slate-900 mt-4 mb-2">24.3 Posting Permissions</h3>
            <p>We never post without your explicit action.</p>

            <h3 className="text-xl font-semibold text-slate-900 mt-4 mb-2">24.4 Disconnection</h3>
            <p>You may disconnect anytime via:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>your CreaVideos account, or</li>
              <li>Meta settings.</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-900 mt-4 mb-2">24.5 Meta Compliance</h3>
            <p>
              Use must comply with Meta policies. We are not responsible for their platform changes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">25. Contact</h2>
            <p>CreaVideos</p>
            <p>
              Email: <a className="text-teal-600 hover:text-teal-700" href="mailto:reno@creavideos.online">reno@creavideos.online</a>
            </p>
            <p>
              Website: <a className="text-teal-600 hover:text-teal-700" href="https://www.creavideos.online">https://www.creavideos.online</a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
